import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  type Theme,
} from '@mui/material';
import { isValidElement, type KeyboardEvent, type MouseEvent, type ReactElement, type ReactNode } from 'react';

import { fontStacks } from '../tokens';
import { displayValue, type DisplayableValue } from './displayValue';

/**
 * Tableau du back-office (maquette « Tableau »), avec repli en cartes empilees
 * sous 600 px.
 *
 * Il existe pour trois regles que chaque ecran oublierait a son tour :
 *
 * 1. **Une cellule vide se lit « — »** : `render` peut rendre `null`, une chaine
 *    vide ou un nombre absent, le tableau ecrit le tiret. Jamais un zero
 *    invente, jamais une case blanche qu'on prendrait pour un chargement.
 * 2. **Sous 600 px, plus de defilement horizontal** : un tableau de six colonnes
 *    sur un telephone se lit une colonne a la fois, en perdant l'en-tete. Chaque
 *    ligne devient une carte qui repete ses intitules (`<dl>`). La premiere
 *    colonne — ou celle marquee `primary` — en est le titre.
 * 3. **Une ligne selectionnable l'est au clavier** : focus par Tab, selection
 *    par Entree ou Espace, ligne courante annoncee (`aria-current`), focus
 *    visible. Un clic sur un lien ou un bouton **dans** la ligne garde son
 *    propre effet et ne selectionne pas la ligne.
 *
 * Pas de tri ni de pagination ici : trier une page serveur est un mensonge
 * visuel (ADR 0024), et la pagination depend de ce que la route rend.
 */
export interface DataTableColumn<R> {
  readonly key: string;
  readonly header: string;
  /** Contenu de la cellule ; `null`, `undefined` ou `''` s'affichent « — ». */
  readonly render: (row: R) => DisplayableValue | ReactElement;
  readonly align?: 'left' | 'right';
  /** Identifiant ou code : police a chasse fixe, relue caractere par caractere. */
  readonly mono?: boolean;
  /** Titre de la carte en vue empilee. Par defaut, la premiere colonne. */
  readonly primary?: boolean;
}

export interface DataTableProps<R> {
  /** Nom du tableau pour les lecteurs d'ecran (`aria-label`). */
  readonly label: string;
  readonly columns: readonly DataTableColumn<R>[];
  readonly rows: readonly R[];
  readonly getRowId: (row: R) => string;
  /** Rend les lignes selectionnables (ouvrir le detail, par exemple). */
  readonly onRowSelect?: (row: R) => void;
  readonly selectedRowId?: string | null;
  /** Phrase affichee a la place des lignes quand il n'y en a aucune. */
  readonly emptyMessage?: string;
}

const renderCell = <R,>(column: DataTableColumn<R>, row: R): ReactNode => {
  const content = column.render(row);

  return isValidElement(content) ? content : displayValue(content);
};

/** Le clic visait-il un controle place dans la ligne (lien, bouton, champ) ? */
const targetsNestedControl = (event: MouseEvent<HTMLElement>): boolean => {
  const control = (event.target as HTMLElement).closest('a, button, input, select, textarea, [role="button"]');

  return control !== null && control !== event.currentTarget;
};

const selectionProps = <R,>(
  row: R,
  selected: boolean,
  onRowSelect: ((row: R) => void) | undefined,
): Record<string, unknown> =>
  onRowSelect === undefined
    ? {}
    : {
        tabIndex: 0,
        'aria-current': selected ? 'true' : undefined,
        onClick: (event: MouseEvent<HTMLElement>) => {
          if (!targetsNestedControl(event)) {
            onRowSelect(row);
          }
        },
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onRowSelect(row);
          }
        },
      };

const selectableSx = {
  cursor: 'pointer',
  '&:hover': { bgcolor: 'tokens.surfaceMuted' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: '-2px' },
} as const;

export const DataTable = <R,>({
  label,
  columns,
  rows,
  getRowId,
  onRowSelect,
  selectedRowId = null,
  emptyMessage = 'Aucun élément à afficher.',
}: DataTableProps<R>): ReactElement => {
  const stacked = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  if (rows.length === 0) {
    return (
      <Typography sx={{ py: 3, textAlign: 'center', color: 'tokens.textMuted', fontSize: 14 }}>
        {emptyMessage}
      </Typography>
    );
  }

  const titleColumn = columns.find((column) => column.primary === true) ?? columns[0];
  const selectedBg = { bgcolor: 'tokens.primarySoft' } as const;

  if (stacked) {
    return (
      <Box component="ul" aria-label={label} sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.5 }}>
        {rows.map((row) => {
          const id = getRowId(row);
          const selected = id === selectedRowId;

          return (
            <Box
              component="li"
              key={id}
              {...selectionProps(row, selected, onRowSelect)}
              sx={[
                { p: 2, borderRadius: '12px', border: 1, borderColor: 'tokens.border', bgcolor: 'tokens.surface' },
                onRowSelect === undefined ? {} : selectableSx,
                selected ? selectedBg : {},
              ]}
            >
              {titleColumn === undefined ? null : (
                <Typography component="div" sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}>
                  {renderCell(titleColumn, row)}
                </Typography>
              )}
              <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(96px, 40%) 1fr', gap: 0.75 }}>
                {columns
                  .filter((column) => column !== titleColumn)
                  .map((column) => (
                    <Box key={column.key} sx={{ display: 'contents' }}>
                      <Box component="dt" sx={{ fontSize: 12, fontWeight: 700, color: 'tokens.textMuted' }}>
                        {column.header}
                      </Box>
                      <Box
                        component="dd"
                        sx={{ m: 0, fontSize: 14, minWidth: 0, overflowWrap: 'anywhere', fontFamily: column.mono === true ? fontStacks.mono : undefined }}
                      >
                        {renderCell(column, row)}
                      </Box>
                    </Box>
                  ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <TableContainer sx={{ border: 1, borderColor: 'tokens.border', borderRadius: '12px' }}>
      <Table aria-label={label}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.key} align={column.align ?? 'left'} sx={{ height: 40, py: 0 }}>
                {column.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const id = getRowId(row);
            const selected = id === selectedRowId;

            return (
              <TableRow
                key={id}
                {...selectionProps(row, selected, onRowSelect)}
                sx={[onRowSelect === undefined ? {} : selectableSx, selected ? selectedBg : {}, { '&:last-child td': { borderBottom: 0 } }]}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align ?? 'left'}
                    sx={{ height: 52, py: 1, fontSize: 14, fontFamily: column.mono === true ? fontStacks.mono : undefined }}
                  >
                    {renderCell(column, row)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
